import { oc } from 'ts-optchain';
import _ from 'lodash';

import { Duration, LocalDate, LocalDateTime, LocalTime } from 'js-joda';
import { NO_RESTRICTIONS } from 'components/EmailPolicyChoice';
import { PurchaseOrderKeyValue } from 'actions/ReservationActions';
import { RefundTypes } from 'constants/RefundTypes';
import { SharedAppConstants } from '@tock/shared-app-constants';
import { capitalizeName } from '@tock/shared/utils/patron';
import { core, payloadToJSON, safeToNumber, toJSON, toJSONArray } from '@tock/coretypes';
import { dashboard } from 'types';
import { fastParseDateTime } from 'utils/timeUtils';
import { isImported } from 'utils/purchaseUtils';
import { tocktix } from '@tock/protos/dist/types/core';
import { visitDietaryNotes, visitGeneralNotes } from 'utils/calendarItemDetailsUtils';
import AppConstants from 'constants/AppConstants';
import HandSellType from 'constants/HandSellType';
import protos from 'utils/protos';
import purchaseOrderHelpers from 'utils/purchaseOrderHelpers';

const { Discount } = protos;

export interface DeliveryInformation {
  streetAddress: string;
  aptSuite?: string;
  city: string;
  state: string;
  isoCountryCode: string;
  zipCode: string;
  phoneCountryCode: string;
  phone: string;
  deliveryInstructions?: string;
}

export interface EditedNotes {
  booking?: string;
  dietary?: string;
}
/**
 * Fields used during hand sell flow
 */
export interface AdditionalPurchaseOrderFields {
  lockedForDuration?: Duration;
  // For a new purchase order, the amount of cents to comp on the new total.
  newPostTotalDiscount?: core.IDiscount;
  // For a new purchase order
  promoCodeDiscount?: core.IDiscount;
  service?: tocktix.messages.Service.Type;
}

export type NewPurchaseOrder = core.IPurchaseOrder & AdditionalPurchaseOrderFields;

interface State {
  basePurchaseOrder?: core.PurchaseOrder;
  completedEmailPurchase?: dashboard.PurchaseVerifyLock;
  completedPurchase?: core.PurchaseOrder;
  editedNotes: EditedNotes;
  eligiblePromoCodes: core.TicketTypePromoCode[];
  giftCardRequestInProgress: boolean;
  guestDietaryNote?: string;
  guestNote?: string;
  guestProfile?: dashboard.GuestProfile;
  guestTags?: string[];
  lockIsStale: boolean;
  makeAvailable: boolean;
  newPurchaseOrder: NewPurchaseOrder;
  numTickets: number;
  paymentCardName: string;
  persistPaymentCard: boolean;
  priceCalculated: boolean;
  priceCalculationInProgress: boolean;
  priceInvalid: boolean;
  promoCodes: core.TicketTypePromoCode[];
  purchase?: core.PastPurchase;
  purchaseOrderKeyValues?: PurchaseOrderKeyValue;
  turnTimeOverrideMin?: number;
  requestInProgress: boolean;
  ticketGroup: core.TicketGroup | null;
  ui: {
    isEditing: boolean;
    isHandSelling: boolean;
    handSellType: HandSellType;
    isTablesideModal?: boolean;
    patronSpecified: boolean;
    refundType?: RefundTypes;
  };
  visitTags?: string[];
}

export const defaultPatron: core.IPatron = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  phoneCountryCode: '',
  zipCode: '',
};

export const initialState: State = {
  basePurchaseOrder: undefined,
  completedPurchase: undefined,
  editedNotes: {
    booking: undefined,
    dietary: undefined,
  },
  eligiblePromoCodes: [],
  giftCardRequestInProgress: false,
  guestDietaryNote: undefined,
  guestNote: undefined,
  guestProfile: undefined,
  guestTags: undefined,
  makeAvailable: true,
  lockIsStale: false,
  newPurchaseOrder: {
    newPostTotalDiscount: undefined,
    adminOverride: true,
    ticketCount: 0,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ticketSubsetId: undefined as any,
    fee: [],
    customChargeOrder: [],
    discount: [],
    patron: { ...defaultPatron },
    dinerPatron: [{ ...defaultPatron }],
    paymentCard: {
      nonceToken: '',
      maskedNumber: '',
      brand: '',
      name: '',
    },
    compAmount: 0,
    purchasedTaxLineItem: [],
    refundOnDowngrade: undefined,
    subtotal: 0,
    taxRate: 0.0,
    tax: 0,
    taxStrategy: 'SINGLE_TAX',
    serviceChargeRate: 0.0,
    serviceCharge: 0,
    selectedGratuityRate: 0.0,
    gratuityCents: 0,
    totalPrice: 0,
    deferredPayment: false,
    lockedForDuration: undefined,
    emailPolicy: NO_RESTRICTIONS,
    promoCodeDiscount: undefined,
  },
  numTickets: 0,
  paymentCardName: '',
  persistPaymentCard: false,
  priceCalculated: true,
  priceCalculationInProgress: false,
  priceInvalid: false,
  promoCodes: [],
  purchase: undefined,
  purchaseOrderKeyValues: undefined,
  requestInProgress: false,
  ticketGroup: null,
  turnTimeOverrideMin: undefined,
  ui: {
    isEditing: false,
    isHandSelling: false,
    handSellType: HandSellType.CHARGE_NOW,
    patronSpecified: false,
    refundType: undefined,
  },
  visitTags: undefined,
};

function computeNotes(noteRecord: EditedNotes): dashboard.IBookingNote[] {
  const notes: dashboard.IBookingNote[] = [];
  notes.push({
    note: noteRecord.booking,
    noteType: 'GENERAL',
  });
  notes.push({
    note: noteRecord.dietary,
    noteType: 'DIETARY',
  });
  return notes;
}

export default function reducer(state: State = initialState, action: BaseTockAction): State {
  const { type, payload, context } = action;
  switch (type) {
    case AppConstants.HAND_SELL_CLEAR_ALL: {
      // leave completed purchase there if it exists for connecting
      // handsell and calendar/service via purchase id
      if (state.completedPurchase) {
        return {
          ...initialState,
          completedPurchase: state.completedPurchase,
        };
      }
      if (state.completedEmailPurchase) {
        return {
          ...initialState,
          completedEmailPurchase: state.completedEmailPurchase,
        };
      }
      return {
        ...initialState,
      };
    }

    case AppConstants.HAND_SELL_OPEN_DONE: {
      const {
        numTickets,
        patron,
        handSellType,
        ticketSubsetId,
        dateTime,
        tableIdsToBook,
        ticketTypeId,
        seatingAreaId,
      } = context;

      const ticketGroup = toJSON<core.TicketGroup>(payload);
      const initNewPurchaseOrder = purchaseOrderHelpers.initialNewPurchaseOrder({
        ticketGroup,
        dateTime,
        ticketSubsetId,
        patron,
        numTickets,
        tableIdsToBook,
        ticketTypeId,
        seatingAreaId,
      });

      return {
        ...state,
        ui: {
          ...state.ui,
          handSellType,
          isHandSelling: true,
          patronSpecified: !!patron,
        },
        editedNotes: {
          booking: oc(visitGeneralNotes(ticketGroup)).note(),
          dietary: oc(visitDietaryNotes(ticketGroup)).note(),
        },
        priceCalculated: false,
        priceCalculationInProgress: false,
        ticketGroup,
        numTickets,
        newPurchaseOrder: { ...state.basePurchaseOrder, ...initNewPurchaseOrder },
        purchaseOrderKeyValues: {
          ...state.purchaseOrderKeyValues,
        },
      };
    }

    case AppConstants.HAND_SELL_EDIT_DONE: {
      const basePurchaseOrder = payloadToJSON<core.PurchaseOrder>(payload);
      const { ticketGroup, purchase, ui } = context;

      const deferredPayment = ui?.handSellType
        ? ui.handSellType === HandSellType.CHARGE_LATER
        : basePurchaseOrder.deferredPayment;

      const turnTimeOverrideMin = purchase?.visit?.turnTimeSecOverride
        ? purchase.visit.turnTimeSecOverride / 60
        : undefined;

      return {
        ...initialState,
        ui: {
          ...initialState.ui,
          handSellType: HandSellType.CHARGE_NOW,
          ...ui,
          isHandSelling: true,
          isEditing: true,
        },
        priceCalculated: false,
        requestInProgress: false,
        ticketGroup,
        numTickets: basePurchaseOrder.ticketCount,
        purchase,
        turnTimeOverrideMin,
        editedNotes: {
          booking: oc(visitGeneralNotes(purchase)).note(),
          dietary: oc(visitDietaryNotes(purchase)).note(),
        },
        basePurchaseOrder,
        newPurchaseOrder: {
          ..._.cloneDeep(basePurchaseOrder),
          increasePurchaseSizeTo: basePurchaseOrder.ticketCount,
          adminOverride: true,
          basePurchaseId: purchase.id,
          patron: purchase.ownerPatron,
          emailPolicy: NO_RESTRICTIONS,
          deferredPayment,
          rescheduleOptions: {
            sourceBehavior:
              // We create slots for imports when they can't be assigned to a table. Default to removing those when rescheduling
              isImported(purchase) && !purchase.tableId?.length
                ? 'REMOVE'
                : 'KEEP_AND_MAKE_AVAILABLE',
          },
        },
        purchaseOrderKeyValues: {
          ...state.purchaseOrderKeyValues,
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_NUM_TICKETS: {
      const numTickets = payload;
      const increasePurchaseSizeTo = numTickets;
      return {
        ...state,
        priceCalculated: false,
        lockIsStale: true,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          ticketCount: numTickets,
          increasePurchaseSizeTo,
        },
      };
    }

    case AppConstants.HAND_SELL_UPDATE_SEATING_AREA: {
      const { seatingAreaId } = payload;
      return {
        ...state,
        priceCalculated: false,
        lockIsStale: true,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          seatingAreaId,
        },
      };
    }

    case AppConstants.HAND_SELL_UPDATE_DATE_TIME: {
      return {
        ...state,
        lockIsStale: true,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          dateTime: payload,
        },
      };
    }

    case 'HAND_SELL_UPDATE_TIME': {
      const newTime = payload;
      const existingDateTime = state.newPurchaseOrder.dateTime;
      let localDate = LocalDate.now();

      if (existingDateTime) {
        localDate = fastParseDateTime(existingDateTime).toLocalDate();
      }

      return {
        ...state,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          dateTime: LocalDateTime.of(localDate, LocalTime.parse(newTime)).toString(),
        },
      };
    }

    case 'HAND_SELL_UPDATE_DATE': {
      const newDate = payload;
      const existingDateTime = state.newPurchaseOrder.dateTime;
      let localTime = LocalTime.now();

      if (existingDateTime) {
        localTime = fastParseDateTime(existingDateTime).toLocalTime();
      }

      return {
        ...state,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          dateTime: LocalDateTime.of(LocalDate.parse(newDate), localTime).toString(),
        },
      };
    }

    case AppConstants.HAND_SELL_UPDATE_EXCLUDE_FROM_PACING: {
      return {
        ...state,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          excludeFromPacing: payload,
        },
      };
    }

    case AppConstants.HAND_SELL_UPDATE_TURN_TIME: {
      return {
        ...state,
        turnTimeOverrideMin: payload,
      };
    }

    case AppConstants.HAND_SELL_UPDATE_EXPERIENCE: {
      const { ticketTypeId, ticketTypeName, ticketTypePrice, fee, optionData, extraChargeDetails } =
        payload;
      const discount =
        state.newPurchaseOrder.ticketTypeId === ticketTypeId
          ? state.newPurchaseOrder.discount
          : _.filter(
              state.newPurchaseOrder.discount,
              (discount) => !(discount.ticketTypePromoCodeId || discount.promoCode)
            );
      const { serviceChargeRate } = extraChargeDetails;
      const optionOrder = _.filter(state.newPurchaseOrder.optionOrder, (option) => {
        // Fired items can never be removed automatically
        if (option.sentToKitchen) {
          return true;
        }
        return _.some(
          optionData.ticketOptions,
          (ticketOption) => ticketOption.id === option.optionId
        );
      });

      return {
        ...state,
        priceCalculated: false,
        lockIsStale: true,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          ticketTypeId,
          ticketTypeName,
          ticketTypePrice,
          discount,
          skipAutoApplyDiscount: false,
          serviceChargeRate,
          optionOrder,
          fee,
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_BOOKING_NOTE: {
      const newEditedNotes = {
        ...state.editedNotes,
        booking: payload,
      };
      return {
        ...state,
        editedNotes: newEditedNotes,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          bookingNote: computeNotes(newEditedNotes),
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_DIETARY_NOTE: {
      const newEditedNotes = {
        ...state.editedNotes,
        dietary: payload,
      };
      return {
        ...state,
        editedNotes: newEditedNotes,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          bookingNote: computeNotes(newEditedNotes),
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_COMP_AMOUNT: {
      const compAmount = payload;
      return {
        ...state,
        priceCalculated: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          compAmount,
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_POST_TOTAL_ADMIN_COMP_AMOUNT: {
      const discountAmount = payload;

      let previousDiscounts = oc(state.basePurchaseOrder).discount([]);
      if (!_.isNil(state.newPurchaseOrder.promoCodeDiscount)) {
        previousDiscounts = previousDiscounts.concat([state.newPurchaseOrder.promoCodeDiscount]);
      }
      previousDiscounts = previousDiscounts.concat(
        _.filter(state.newPurchaseOrder.discount, (discount) => !!discount.giftCard)
      );

      if (discountAmount === 0) {
        // remove the discount
        return {
          ...state,
          priceCalculated: false,
          newPurchaseOrder: {
            ...state.newPurchaseOrder,
            discount: previousDiscounts,
            newPostTotalDiscount: undefined,
          },
        };
      } else {
        const newPostTotalDiscount = {
          amountCents: discountAmount,
          discountType: Discount.DiscountType.POST_TOTAL_ADMIN_COMP,
          description: 'Discount',
        };

        const discounts = _.concat(previousDiscounts, newPostTotalDiscount);
        return {
          ...state,
          priceCalculated: false,
          newPurchaseOrder: {
            ...state.newPurchaseOrder,
            discount: discounts,
            newPostTotalDiscount,
          },
        };
      }
    }
    case AppConstants.HAND_SELL_UPDATE_OPTIONS: {
      const optionOrder = payload;
      return {
        ...state,
        priceCalculated: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          optionOrder,
        },
        ui: {
          ...state.ui,
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_PROMOCODE: {
      // A purchase order has at most one promo code discount,
      // filter out previous promo code discounts.
      let discount = _.filter(
        state.newPurchaseOrder.discount,
        (discount) => !(discount.ticketTypePromoCodeId || discount.promoCode)
      );
      let promoCodeDiscount = undefined;
      if (payload) {
        discount = _.concat(discount, [payload]);
        promoCodeDiscount = payload;
      }
      return {
        ...state,
        priceCalculated: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          discount,
          skipAutoApplyDiscount: !payload,
          promoCodeDiscount,
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_PATRON: {
      const patron = payload.patron;
      const diner = payload.diner;

      const patronChangedOrIsNew =
        patron.id === undefined ||
        patron.id == null ||
        patron.id !== state.basePurchaseOrder?.patron?.id;
      const discount = patronChangedOrIsNew
        ? _.filter(
            state.newPurchaseOrder.discount,
            (discount) => !(discount.ticketTypePromoCodeId || discount.promoCode)
          )
        : state.newPurchaseOrder.discount;

      patron.firstName = capitalizeName(patron.firstName);
      patron.lastName = capitalizeName(patron.lastName);
      diner.firstName = capitalizeName(diner.firstName);
      diner.lastName = capitalizeName(diner.lastName);
      return {
        ...state,
        priceCalculated: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          patron,
          discount,
          skipAutoApplyDiscount: false,
          dinerPatron: [diner],
        },
      };
    }
    case AppConstants.HAND_SELL_CLEAR_PAYMENT_CARD: {
      return {
        ...state,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          paymentCard: initialState.newPurchaseOrder.paymentCard,
        },
        persistPaymentCard: false,
      };
    }
    case AppConstants.HAND_SELL_UPDATE_PAYMENT_CARD: {
      const { paymentCard, persistPaymentCard } = payload;
      return {
        ...state,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          paymentCard,
        },
        persistPaymentCard,
      };
    }
    case AppConstants.HAND_SELL_UPDATE_PAYMENT_CARD_NAME: {
      const { name } = payload;
      return {
        ...state,
        paymentCardName: name,
      };
    }
    case AppConstants.HAND_SELL_UPDATE_REFUND_TYPE: {
      return {
        ...state,
        priceCalculated: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          refundOnDowngrade: payload === RefundTypes.FULL,
        },
        ui: {
          ...state.ui,
          refundType: payload,
        },
      };
    }
    case AppConstants.HAND_SELL_CLEAR_REFUND_TYPE: {
      return {
        ...state,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          refundOnDowngrade: undefined,
        },
        ui: {
          ...state.ui,
          refundType: undefined,
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_TYPE: {
      const deferredPayment = payload === HandSellType.CHARGE_LATER;
      const isBookByEmailOrSms = payload === HandSellType.EMAIL || payload === HandSellType.SMS;
      return {
        ...state,
        priceCalculated: !isBookByEmailOrSms, // false means it will re-calc, so if isBookByEmailOrSms we want to re-calc
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          deferredPayment,
        },
        ui: {
          ...state.ui,
          handSellType: payload,
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_EMAIL_POLICY: {
      return {
        ...state,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          emailPolicy: payload,
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_AVAILABILITY: {
      return {
        ...state,
        makeAvailable: payload,
      };
    }
    case AppConstants.HAND_SELL_PROMOCODE_LIST_DONE: {
      return {
        ...state,
        promoCodes: toJSONArray<core.TicketTypePromoCode>(payload),
      };
    }
    case AppConstants.HAND_SELL_PROMOCODE_ELIGIBLITY_DONE: {
      return {
        ...state,
        eligiblePromoCodes: toJSONArray<core.TicketTypePromoCode>(payload),
      };
    }
    case AppConstants.HAND_SELL_UPDATE_LOCK_DURATION: {
      return {
        ...state,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          lockedForDuration: payload,
        },
      };
    }
    case AppConstants.HAND_SELL_CLEAR_LOCK_DURATION: {
      return {
        ...state,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          lockedForDuration: undefined,
        },
      };
    }
    case AppConstants.HAND_SELL_CUSTOM_CHARGE_ADD: {
      const customCharge = payload as core.ICustomChargeOrder;

      const newCustomChargeOrder =
        state.newPurchaseOrder.customChargeOrder &&
        state.newPurchaseOrder.customChargeOrder.length > 0
          ? state.newPurchaseOrder.customChargeOrder.slice()
          : [];

      newCustomChargeOrder.push(customCharge);

      return {
        ...state,
        priceCalculated: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          customChargeOrder: newCustomChargeOrder,
        },
      };
    }
    case AppConstants.HAND_SELL_CUSTOM_CHARGE_REMOVE: {
      const customCharge = payload as core.ICustomChargeOrder;

      const currentCustomChargeOrder =
        state.newPurchaseOrder.customChargeOrder &&
        state.newPurchaseOrder.customChargeOrder.length > 0
          ? state.newPurchaseOrder.customChargeOrder.slice()
          : [];

      return {
        ...state,
        priceCalculated: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          customChargeOrder: currentCustomChargeOrder.filter(
            (charge) =>
              !(
                charge.customChargeName === customCharge.customChargeName &&
                charge.priceCents === customCharge.priceCents
              )
          ),
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_GRATUITY: {
      const { selectedGratuityRate, gratuityCents } = payload;
      return {
        ...state,
        priceCalculated: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          gratuityCents: gratuityCents,
          selectedGratuityRate: selectedGratuityRate,
        },
      };
    }
    case AppConstants.HAND_SELL_CALCULATE_PRICE_DONE: {
      const newPurchaseOrder = toJSON<NewPurchaseOrder>(payload);
      newPurchaseOrder.lockedForDuration = state.newPurchaseOrder.lockedForDuration;
      newPurchaseOrder.promoCodeDiscount = state.newPurchaseOrder.promoCodeDiscount;
      newPurchaseOrder.ticketSubsetId = state.newPurchaseOrder.ticketSubsetId;
      newPurchaseOrder.refundOnDowngrade = state.newPurchaseOrder.refundOnDowngrade;
      return {
        ...state,
        priceCalculationInProgress: false,
        priceCalculated: true,
        newPurchaseOrder: {
          ...newPurchaseOrder,
          paymentCard: {
            ...state.newPurchaseOrder.paymentCard,
            ...newPurchaseOrder.paymentCard,
          },
        },
        priceInvalid: false,
      };
    }
    case AppConstants.HAND_SELL_CALCULATE_PRICE_STARTING: {
      return {
        ...state,
        priceCalculationInProgress: true,
      };
    }
    case AppConstants.HAND_SELL_PURCHASE_EMAIL_STARTING:
    case AppConstants.HAND_SELL_PURCHASE_STARTING:
    case AppConstants.HAND_SELL_EDIT_STARTING:
    case SharedAppConstants.PAYMENT_CARD_INSERT_STARTING:
    case SharedAppConstants.PAYMENT_CARD_PAYMENT_INTENT_STARTING: {
      return {
        ...state,
        requestInProgress: true,
      };
    }
    case AppConstants.HAND_SELL_CALCULATE_PRICE_FAILED: {
      return {
        ...state,
        priceCalculated: true,
        priceCalculationInProgress: false,
        priceInvalid: true,
      };
    }
    case AppConstants.HAND_SELL_PURCHASE_EMAIL_DONE: {
      return {
        ...state,
        completedEmailPurchase: payloadToJSON<dashboard.PurchaseVerifyLock>(payload),
        requestInProgress: false,
      };
    }
    case AppConstants.HAND_SELL_PURCHASE_DONE: {
      const completedPurchaseOrder = payloadToJSON<core.PurchaseOrder>(payload);
      return {
        ...state,
        completedPurchase: _.cloneDeep(completedPurchaseOrder),
        newPurchaseOrder: _.cloneDeep(completedPurchaseOrder),
        basePurchaseOrder: _.cloneDeep(completedPurchaseOrder),
        // priceCalculated: false,
        requestInProgress: false,
      };
    }

    case AppConstants.HAND_SELL_CLEAR_COMPLETED_PURCHASE: {
      return {
        ...state,
        completedPurchase: undefined,
        purchaseOrderKeyValues: undefined,
      };
    }

    case AppConstants.HAND_SELL_CLEAR_COMPLETED_PURCHASE_EMAIL: {
      return {
        ...state,
        completedEmailPurchase: undefined,
      };
    }

    case AppConstants.HAND_SELL_PURCHASE_EMAIL_FAILED:
    case AppConstants.HAND_SELL_PURCHASE_FAILED:
    case AppConstants.HAND_SELL_EDIT_FAILED:
    case SharedAppConstants.PAYMENT_CARD_INSERT_FAILED:
    case SharedAppConstants.PAYMENT_CARD_PAYMENT_INTENT_FAILED: {
      return {
        ...state,
        requestInProgress: false,
      };
    }
    case AppConstants.HAND_SELL_GIFT_CARD_APPLY_STARTING: {
      return {
        ...state,
        giftCardRequestInProgress: true,
      };
    }
    case AppConstants.HAND_SELL_GIFT_CARD_APPLY_DONE: {
      const giftCardBalance = payload;
      const { giftCardNumber, giftCardPin, business } = context;
      if (_.isNil(giftCardBalance) || _.isNil(giftCardNumber)) {
        return {
          ...state,
          giftCardRequestInProgress: false,
        };
      }
      const amountToDiscount = _.min([
        context.amountCents, // requested amount to apply
        giftCardBalance, // current gift card balance
        state.newPurchaseOrder.totalPrice, // total price on the purchase
      ]);
      if (amountToDiscount <= 0) {
        return {
          ...state,
          giftCardRequestInProgress: false,
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const discount = _.clone(state.newPurchaseOrder.discount)!;
      discount.push({
        description: `Gift Card Ending in ${giftCardNumber.substr(-4)}`,
        amountCents: amountToDiscount,
        discountType: Discount.DiscountType.CREDIT,
        giftCard: {
          giftCardProvider: business.giftCardProvider,
          externalIdentifier: giftCardNumber,
          externalPin: giftCardPin,
        },
      });
      return {
        ...state,
        priceCalculated: false,
        giftCardRequestInProgress: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          discount,
        },
      };
    }
    case AppConstants.HAND_SELL_GIFT_CARD_APPLY_FAILED: {
      return {
        ...state,
        giftCardRequestInProgress: false,
      };
    }
    case AppConstants.HAND_SELL_FETCH_GUEST_PROFILE_DONE: {
      return {
        ...state,
        guestProfile: payload,
      };
    }
    case AppConstants.HAND_SELL_CLEAR_GUEST_PROFILE: {
      return {
        ...state,
        guestProfile: initialState.guestProfile,
      };
    }
    case AppConstants.HAND_SELL_UPDATE_GUEST_NOTES: {
      const { note, dietaryNote } = payload;
      return {
        ...state,
        guestNote: note,
        guestDietaryNote: dietaryNote,
      };
    }
    case AppConstants.HAND_SELL_CLEAR_GUEST_NOTES: {
      return {
        ...state,
        guestNote: initialState.guestNote,
        guestDietaryNote: initialState.guestDietaryNote,
      };
    }
    case AppConstants.HAND_SELL_UPDATE_VISIT_TAGS: {
      const { visitTags } = payload;
      return {
        ...state,
        visitTags,
      };
    }
    case AppConstants.HAND_SELL_UPDATE_GUEST_TAGS: {
      const { guestTags } = payload;
      return {
        ...state,
        guestTags,
      };
    }
    case AppConstants.HAND_SELL_UPDATE_RESCHEDULE_OPTIONS: {
      return {
        ...state,
        newPurchaseOrder: { ...state.newPurchaseOrder, rescheduleOptions: payload },
      };
    }
    case AppConstants.HAND_SELL_CLEAR_GUEST_TAGS: {
      return {
        ...state,
        guestTags: initialState.guestTags,
      };
    }
    case AppConstants.HAND_SELL_SET_KEY_VALUES: {
      return {
        ...state,
        purchaseOrderKeyValues: {
          ...state.purchaseOrderKeyValues,
          ...payload,
        },
      };
    }
    case AppConstants.HAND_SELL_CLEAR_KEY_VALUES: {
      return {
        ...state,
        purchaseOrderKeyValues: undefined,
      };
    }
    case AppConstants.HAND_SELL_GIFT_CARD_REMOVE: {
      const giftCardNumber = context.giftCardNumber;
      const discount = _.reject(
        state.newPurchaseOrder.discount,
        (discount) => !!discount.giftCard && discount.giftCard.externalIdentifier === giftCardNumber
      );
      return {
        ...state,
        priceCalculated: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          discount,
        },
      };
    }
    case AppConstants.HAND_SELL_INCREASE_SIZE_FLAG: {
      return {
        ...state,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          increaseCommunalTableSize: true,
        },
      };
    }
    case AppConstants.HAND_SELL_UPDATE_DELIVERY_INFORMATION: {
      const delivery: core.IDelivery = {
        location: {
          address: payload.streetAddress,
          address2: payload.aptSuite,
          city: payload.city,
          state: payload.state,
          country: payload.isoCountryCode,
          zipCode: payload.zipCode,
        },
        phoneCountryCode: payload.phoneCountryCode,
        phone: payload.phone,
        deliveryInstructions: payload.deliveryInstructions,
      };
      return {
        ...state,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          delivery,
        },
      };
    }
    case AppConstants.HAND_SELL_LOCK_DONE: {
      const ticketGroup: core.ITicketGroup = payload;
      return {
        ...state,
        lockIsStale: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          ticketSubsetId: safeToNumber(ticketGroup?.ticketSubset?.[0]?.id),
          ticketGroupId: safeToNumber(ticketGroup?.id),
        },
      };
    }
    case AppConstants.HAND_SELL_UNLOCK_DONE: {
      return {
        ...state,
        lockIsStale: false,
        newPurchaseOrder: {
          ...state.newPurchaseOrder,
          ticketSubsetId: undefined,
        },
      };
    }
    default:
      return state;
  }
}
